import { Music } from './Music';
import { Sfx } from './Sfx';

type AdBreakType = 'next' | 'pause' | 'start' | 'reward';
type AdProvider = 'placeholder' | 'google';

interface AdBreakOptions {
  type: AdBreakType;
  name: string;
  beforeAd?: () => void;
  afterAd?: () => void;
  adBreakDone?: () => void;
}

interface AdConfigOptions {
  preloadAdBreaks?: 'on' | 'off' | 'auto';
  sound?: 'on' | 'off';
}

interface GoogleFcCallbackQueue {
  push(callback: Record<string, () => void>): unknown;
}

interface GoogleFc {
  callbackQueue: GoogleFcCallbackQueue;
  showRevocationMessage?: () => void;
}

interface InterstitialOptions {
  placement: string;
  beforeAd: () => void;
  afterAd: () => void;
  showPlaceholder: (complete: () => void) => void;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    adBreak?: (options: AdBreakOptions) => void;
    adConfig?: (options: AdConfigOptions) => void;
    googlefc?: GoogleFc;
  }
}

let provider: AdProvider = 'placeholder';
let initialized = false;

function currentSoundSetting(): 'on' | 'off' {
  return Music.isEnabled() || Sfx.isEnabled() ? 'on' : 'off';
}

function installGoogleQueue(): void {
  window.adsbygoogle = window.adsbygoogle ?? [];
  const enqueue = (options: AdBreakOptions | AdConfigOptions): void => {
    window.adsbygoogle?.push(options);
  };
  window.adBreak = (options) => enqueue(options);
  window.adConfig = (options) => enqueue(options);
  window.googlefc = window.googlefc ?? { callbackQueue: [] };
}

/**
 * Enables the Google H5 Games Ad Placement API only when production activation
 * and a publisher ID are both explicitly supplied. Placeholder ads are the
 * safe default for development, review builds, and pre-approval releases.
 */
export function initAds(): void {
  if (initialized) return;
  initialized = true;

  const client = import.meta.env.VITE_GOOGLE_ADSENSE_CLIENT?.trim();
  const enabled = import.meta.env.VITE_GOOGLE_H5_ADS_ENABLED === 'true';
  if (!client || !enabled) return;

  provider = 'google';
  installGoogleQueue();
  window.adConfig?.({ preloadAdBreaks: 'on', sound: currentSoundSetting() });

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.dataset.adClient = client;
  if (import.meta.env.VITE_GOOGLE_H5_ADS_TEST_MODE === 'true') {
    script.dataset.adbreakTest = 'on';
  }
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
  document.head.appendChild(script);
}

export function updateAdSoundSetting(): void {
  if (provider === 'google') {
    window.adConfig?.({ sound: currentSoundSetting() });
  }
}

export function hasGoogleAdPrivacyChoices(): boolean {
  return provider === 'google';
}

/** Reopens Google's certified EU consent message through its supported queue. */
export function showGoogleAdPrivacyChoices(): boolean {
  if (provider !== 'google' || !window.googlefc) return false;
  window.googlefc.callbackQueue.push({
    CONSENT_DATA_READY: () => window.googlefc?.showRevocationMessage?.()
  });
  return true;
}

export function shouldShowInterstitialAfterLevel(levelId: number, stars: number): boolean {
  return stars > 0 && levelId > 0 && levelId % 3 === 0;
}

export function showInterstitial(options: InterstitialOptions): Promise<void> {
  if (provider === 'placeholder') {
    options.beforeAd();
    return new Promise((resolve) => {
      options.showPlaceholder(() => {
        options.afterAd();
        resolve();
      });
    });
  }

  return new Promise((resolve) => {
    const adBreak = window.adBreak;
    if (!adBreak) {
      resolve();
      return;
    }

    let adStarted = false;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      if (adStarted) options.afterAd();
      resolve();
    };

    adBreak({
      type: 'next',
      name: options.placement,
      beforeAd: () => {
        adStarted = true;
        options.beforeAd();
      },
      afterAd: () => {
        if (!adStarted) return;
        adStarted = false;
        options.afterAd();
      },
      adBreakDone: finish
    });
  });
}
